<template>
  <div>
    <client-only>
      <AuthModal
        ref="auth"
        login-title="Login to add this credit to your account!"
        register-title="Register to add this credit to your account!"
      />
    </client-only>
  </div>
</template>

<script>
import AuthModal from '@/components/modals/AuthModal'

export default {
  components: { AuthModal },

  computed: {
    /**
     * An Array of deeplinked Credits to add to the users account.
     *
     * @return {array}
     */
    creditsToAdd() {
      let credits = []
      const query = this.$route.query.credit

      // If we have a credit query, add it to the Array
      if (query) {
        credits = typeof query === 'string' ? [query] : query
      }

      // Make sure there are no blank credits in there!
      return credits.filter(Boolean)
    },
  },

  /**
   * When the component is mounted, we should apply any credits if they appear in the URL.
   * We end up in a sitation where 3 paths are possible:
   *
   * 1. The user is authed, and a credit is deep linked.
   * 2. The user is not authed, but a credit is deep linked.
   * 3. There is no credit deep linked.  The auth status does not matter in this case.
   *
   * In the first case, we can immediately attempt to apply the credit to the users account. If
   * that's unsuccessful, then we probably have an issue with the credit ID, and should show a message
   * to the user.
   *
   * In the second case, we need to set an event handler for when the user logs in.  Additionally, we
   * trigger the auth popup to capture the users information and allow them to add the credit right away.
   *
   * Lastly, we can just ignore
   */
  async mounted() {
    // We have no credits, see ya! 👋🏻
    if (!this.creditsToAdd.length) {
      return
    }

    const auth = await this.$getAuthInstance()
    const user = await auth.currentAuthenticatedUser().catch(() => null)

    if (user) {
      this.addCreditsToAccount()
    } else {
      await new Promise((resolve, reject) => {
        this.$refs.auth.handleModalOpenEvent({
          scope: this.modalScope,
          canClose: false,
          modalType: 'register',
          resolve,
          reject,
        })
      })

      this.addCreditsToAccount()
    }
  },

  methods: {
    /**
     * Loop through our credits Array and try to add each one to the users account.
     */
    async addCreditsToAccount() {
      await Promise.all(
        // We're running an Array.map so we can await and make sure it's finished before clearing the route
        this.creditsToAdd.map(async id => {
          try {
            await this.$store.dispatch('payments/assignCreditToUser', id)
          } catch (e) {
            this.$toast.error(
              'This credit is expired, invalid, or has already been redeemed.'
            )
          }
        })
      )

      // Clear the credit query param
      this.$router.replace(this.$route.path)
    },
  },
}
</script>
